import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import cls from 'classnames';

import { Text, TextButtonPriority } from 'wix-ui-tpa';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { groupsTabClicked } from '@wix/bi-logger-groups/v2';
import { safeJsonParse } from 'wix-rich-content-common';
import { fromDraft } from 'ricos-content/libs/converters';
import { getText } from 'ricos-content/libs/content-application';

import { UISref } from 'common/router';
import { skeleton } from 'common/components/Skeleton';
import { Box } from 'common/components/Box/Box';
import { CustomTextButton } from 'common/components/CustomTextButton';

import {
  selectApplication,
  selectGroup,
  selectIsGroupLoading,
} from 'store/groups/selectors';
import { GroupAppKey } from 'store/groups/types';

import * as discussionStyles from '../DiscussionPage.st.css';

import { classes } from './AboutGroup.st.css';

interface IAboutGroupProps {
  groupId: string;
  maxLength?: number;
}

export function AboutGroup(props: IAboutGroupProps) {
  const { groupId } = props;

  const { isMobile } = useEnvironment();
  const { t } = useTranslation();
  const bi = useBi();

  const group = useSelector(selectGroup(groupId));
  const isLoading = useSelector(selectIsGroupLoading(groupId));
  const about = useSelector(
    selectApplication({
      groupId,
      application: GroupAppKey.ABOUT_APP,
    }),
  );

  const description = useMemo(() => {
    const content = safeJsonParse(group.description as string);

    return content
      ? getText(fromDraft(content)).join(' ')
      : group.description || '';
  }, [group.description]);

  return (
    <Box article withSideBorders={!isMobile} data-hook="about-widget">
      <Text
        className={cls(discussionStyles.classes.widgetHeader, classes.header)}
      >
        {about?.customName || t('groups-web.discussion.about.title')}
      </Text>

      {(() => {
        if (isLoading) {
          return (
            <Text className={classes.description}>
              <span className={skeleton}>
                Lorem ipsum dolor sit amet consectetur.
              </span>
            </Text>
          );
        }

        return (
          <Text className={classes.description}>
            {description.substring(0, props.maxLength!) ||
              t('groups-web.discussion.about.description')}
            {(() => {
              if (description.length < props.maxLength!) {
                return null;
              }

              return (
                <>
                  ...
                  <UISref state="group.about" params={{ slug: group.slug }}>
                    <CustomTextButton
                      as="a"
                      className={classes.readMore}
                      onClick={handleReadMoreClick}
                      priority={TextButtonPriority.primary}
                    >
                      {t('groups-web.more')}
                    </CustomTextButton>
                  </UISref>
                </>
              );
            })()}
          </Text>
        );
      })()}
    </Box>
  );

  function handleReadMoreClick() {
    bi.report(
      groupsTabClicked({
        group_id: group.id as string,
        origin: 'pinned_window',
        name: 'about',
      }),
    );
  }
}

AboutGroup.defaultProps = {
  maxLength: 60,
};
